import React, { useState, useReducer } from 'react';
import { leverageMultiplier } from '../../../helpers';
import ExchangeSwapContent from './ExchangeSwapContent';
import useChartState from '../../../context/chart/useChartState';
import useTokenDetails from '../../../hooks/web3/useTokenDetails';
import { EnabledChains, OrderBookType, Validators } from '../../../config/constants';
import { useWeb3React } from '@web3-react/core';
import { config } from '../../../config/config';
import useReferralState from '../../../context/referral/useReferralState';
import { ethers } from 'ethers';
import useTradeState from '../../../context/trade/useTradeState';
import { formatEther, parseEther, parseUnits } from 'ethers/lib/utils';
import OrderExpiryDropdown from '../../../components/OrderExpiryDropdown';
import LimitPriceInput from '../../../components/LimitPriceInput';
import useCreateOrder from '../../../hooks/web3/trade/useCreateOrder';

const ExchangeLimit: React.FC = () => {
    const [withExpiryDate, toggleWithExpiryDate] = useReducer((prev) => !prev, false);
    const [expiryDate, setExpiryDate] = useState<Date | undefined>();

    const { referral } = useReferralState();
    const { account, chainId } = useWeb3React();
    const { selectedTradePair } = useChartState();
    const { collateral, leverage, isShort, limitPrice, setLimitPrice } = useTradeState();
    const createOrder = useCreateOrder();

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const validators: Validators[] = [
        {
            errorCondition:
                !limitPrice || parseUnits(limitPrice, config.orderBookDecimal).isZero(),
            message: 'Limit Price is required!',
        },
        {
            errorCondition: withExpiryDate && !expiryDate,
            message: 'Expiry Date is required!',
        },
    ];

    const getBankruptcyPrice = (latestPrice: string) => {
        return formatEther(
            parseEther(latestPrice)
                .mul(
                    parseEther('1')[isShort ? 'add' : 'sub'](
                        parseEther('1')
                            .mul(parseUnits('1', 2))
                            .div(parseUnits(leverage, 2))
                    )
                )
                .div(parseEther('1'))
        );
    };

    const handleConfirm = async () => {
        if (!selectedTradePair) throw new Error('No Pool selected');

        const referrer =
            !referral?.address ||
            referral?.address.toLowerCase() === account?.toLowerCase()
                ? ethers.constants.AddressZero
                : referral?.address;

        await createOrder(
            OrderBookType.OPEN,
            {
                tradePair: selectedTradePair.address,
                margin: parseUnits(collateral, decimals).toString(),
                leverage: leverageMultiplier(leverage).toString(),
                isShort,
                referrer: referrer.toLowerCase(),
                whitelabelAddress: ethers.constants.AddressZero,
            },
            'OpenPositionOrder',
            isShort,
            getBankruptcyPrice,
            { limitPrice, expiryDate, tradePair: selectedTradePair }
        );
    };

    return (
        <ExchangeSwapContent handleConfirm={handleConfirm} validators={validators}>
            <LimitPriceInput
                selectedTradePair={selectedTradePair}
                onChange={setLimitPrice}
                value={limitPrice}
            />
            <OrderExpiryDropdown
                value={expiryDate}
                orderExpiryChecked={withExpiryDate}
                onChange={setExpiryDate}
                toggleOrderExpiry={toggleWithExpiryDate}
            />
        </ExchangeSwapContent>
    );
};

export default ExchangeLimit;
